import ServiceLayout from "@/components/ServiceLayout";
import { QuoteModal } from "@/components/QuoteModal";
import { Button } from "@/components/ui/button";
import { Phone } from "lucide-react";

export default function Moebelmontage() {
  return (
    <ServiceLayout
      title="Möbelmontage in Augsburg"
      description="Präzise Möbelmontage für jedes Zuhause – schnell, sauber und zuverlässig, damit alles perfekt sitzt und sofort nutzbar ist."
      image="https://images.leadconnectorhq.com/image/f_webp/q_80/r_1200/u_https://assets.cdn.filesafe.space/Zrqvy6cNzSbnzDV55cv1/media/6936df9350387f29d144e356.jpg"
      content={
        <>
          <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight">
            Fachgerechter Aufbau für Ihre Möbel
          </h2>
          <p className="text-lg text-neutral-600">
            Der Aufbau neuer Möbel kann frustrierend und zeitaufwendig sein. Unsere erfahrenen Monteure in Augsburg übernehmen diese Aufgabe gerne für Sie. Wir bauen Schränke, Betten, Regalsysteme und komplette Küchen präzise und fachgerecht auf.
          </p>
          <p className="text-lg text-neutral-600">
            Auch bei Umzügen kümmern wir uns um die fachkundige Demontage und den anschließenden Wiederaufbau Ihrer geliebten Möbelstücke. Wir bringen das nötige Werkzeug und Know-how mit, um jedes Möbelstück sicher und stabil zu montieren.
          </p>
          <ul className="space-y-3 mt-6">
            {[
              "Aufbau von Neumöbeln aller Marken",
              "Fachgerechte Demontage bei Umzügen",
              "Küchenmontage & Anpassungen",
              "Schnell, sauber und stabil"
            ].map((item, i) => (
              <li key={i} className="flex items-center gap-3 text-neutral-800 font-medium">
                <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-primary shrink-0">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                </div>
                {item}
              </li>
            ))}
          </ul>
        </>
      }
      extendedContent={
        <div className="bg-white">
          <section className="py-16 bg-neutral-50">
            <div className="container mx-auto px-4 md:px-6">
              <div className="max-w-4xl mx-auto space-y-8">
                <div>
                  <h2 className="font-heading text-3xl font-bold mb-4">Professionelle Möbelmontage in Augsburg – Für Privat und Gewerbe</h2>
                  <p className="text-neutral-600 leading-relaxed mb-4">
                    Egal ob Sie gerade neue Möbel gekauft haben oder im Rahmen eines Umzugs Ihre bestehende Einrichtung abbauen und wieder aufbauen müssen – die Möbelmontage erfordert Geduld, handwerkliches Geschick und das richtige Werkzeug. Als Ihr zuverlässiger Partner in Augsburg bieten wir Ihnen einen professionellen Montageservice, der Ihnen Zeit, Nerven und Mühe erspart. Unsere geschulten Monteure kennen sich mit den Systemen aller gängigen Möbelhersteller bestens aus und garantieren einen makellosen und stabilen Aufbau.
                  </p>
                  <p className="text-neutral-600 leading-relaxed mb-4">
                    Ein falsch montierter Schrank oder ein wackeliges Bett sind nicht nur ärgerlich, sondern können auch die Lebensdauer Ihrer Möbel erheblich verkürzen. Wir setzen auf Präzision und Sorgfalt. Jedes Scharnier wird exakt eingestellt, jede Schraube perfekt festgezogen und jedes Möbelstück exakt ausgerichtet. So stellen wir sicher, dass Ihre Einrichtung nicht nur fantastisch aussieht, sondern auch langfristig einwandfrei funktioniert und sicher steht.
                  </p>
                  <div className="pt-4 pb-2 flex flex-col sm:flex-row items-start gap-4">
                    <QuoteModal>
                      <Button size="lg" className="font-bold text-base px-8 py-6 rounded-none bg-primary text-white hover:bg-primary/90 w-full sm:w-auto">
                        Kostenloses Angebot anfordern
                      </Button>
                    </QuoteModal>
                    <Button size="lg" variant="secondary" asChild className="w-full sm:w-auto">
                      <a href="tel:+4915888640762">
                        <Phone className="w-5 h-5 mr-2" />
                        +49 15888 640762
                      </a>
                    </Button>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-8 pt-8">
                  <div className="bg-white p-8 border border-neutral-200">
                    <h3 className="font-heading text-xl font-bold mb-3">Neumöbelmontage aller Marken</h3>
                    <p className="text-neutral-600 leading-relaxed">
                      Sie haben neue Möbel bei IKEA, XXXLutz, Segmüller oder einem anderen Einrichtungshaus gekauft? Wir übernehmen den Aufbau für Sie. Ob komplexe Kleiderschranksysteme, Boxspringbetten, Wohnwände oder Büromöbel – wir montieren Ihre neuen Schätze fachgerecht und zügig. Auf Wunsch entsorgen wir im Anschluss auch gleich das anfallende Verpackungsmaterial umweltgerecht.
                    </p>
                  </div>
                  <div className="bg-white p-8 border border-neutral-200">
                    <h3 className="font-heading text-xl font-bold mb-3">Küchenmontage & Anpassung</h3>
                    <p className="text-neutral-600 leading-relaxed">
                      Die Montage einer Küche ist die Königsdisziplin der Möbelmontage. Unsere Experten übernehmen den kompletten Aufbau Ihrer neuen Einbauküche oder passen Ihre bestehende Küche nach einem Umzug an die neuen Räumlichkeiten an. Dazu gehören auch der passgenaue Zuschnitt von Arbeitsplatten und die fachgerechte Installation aller Schränke und Blenden.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="py-16 bg-white">
            <div className="container mx-auto px-4 md:px-6">
              <div className="max-w-4xl mx-auto space-y-8">
                <div>
                  <h2 className="font-heading text-3xl font-bold mb-4">Demontage & Remontage bei Ihrem Umzug</h2>
                  <p className="text-neutral-600 leading-relaxed mb-4">
                    Ein Umzug ist der häufigste Grund für die Demontage von Möbeln. Besonders bei großen und sperrigen Stücken wie Kleiderschränken oder großen Doppelbetten ist ein Abbau unumgänglich, um diese sicher transportieren zu können. Unser Service in Augsburg umfasst die schonende Demontage Ihrer Möbel in der alten Wohnung. Wir verpacken alle Einzelteile und Schrauben sicher, sodass beim Transport nichts verloren geht oder beschädigt wird.
                  </p>
                  <p className="text-neutral-600 leading-relaxed mb-4">
                    Am neuen Wohnort angekommen, kümmern wir uns um die sofortige Remontage. Wir stellen Ihre Möbel genau dort auf, wo Sie sie haben möchten, und richten sie perfekt aus. Dieser Service ist besonders wertvoll, da er sicherstellt, dass Sie bereits am ersten Abend in Ihrem neuen Zuhause in Ihrem gewohnten Bett schlafen und Ihre Kleidung direkt einräumen können.
                  </p>
                  <div className="pt-4 pb-2 flex flex-col sm:flex-row items-start gap-4">
                    <QuoteModal>
                      <Button size="lg" className="font-bold text-base px-8 py-6 rounded-none bg-primary text-white hover:bg-primary/90 w-full sm:w-auto">
                        Jetzt beraten lassen
                      </Button>
                    </QuoteModal>
                  </div>
                </div>

                <div className="bg-primary/5 p-8 border-l-4 border-primary mt-8">
                  <h3 className="font-heading text-2xl font-bold mb-3">Werkzeug & Know-how – Wir bringen alles mit</h3>
                  <p className="text-neutral-700 leading-relaxed">
                    Für eine professionelle Möbelmontage reicht ein einfacher Schraubenzieher oft nicht aus. Unsere Monteure sind mit hochwertigem Profi-Werkzeug ausgestattet. Von leistungsstarken Akkuschraubern über Wasserwaagen und Laser-Messgeräte bis hin zu speziellem Montagewerkzeug – wir haben für jede Schraube und jedes System das passende Instrument dabei. Das garantiert nicht nur einen schnellen Aufbau, sondern schont auch das Material Ihrer wertvollen Möbel.
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section className="py-16 bg-neutral-950 text-white">
            <div className="container mx-auto px-4 md:px-6">
              <div className="max-w-4xl mx-auto text-center">
                <h2 className="font-heading text-3xl font-bold mb-6">Ihre Vorteile mit unserem Montageservice</h2>
                <div className="grid sm:grid-cols-3 gap-8 mt-12 text-left">
                  <div>
                    <div className="w-12 h-12 bg-primary flex items-center justify-center mb-4">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path></svg>
                    </div>
                    <h3 className="font-bold text-lg mb-2">Garantiert Schadensfrei</h3>
                    <p className="text-neutral-400">Dank unserer Erfahrung und unserem Versicherungsschutz ist Ihr Mobiliar zu 100% abgesichert.</p>
                  </div>
                  <div>
                    <div className="w-12 h-12 bg-primary flex items-center justify-center mb-4">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
                    </div>
                    <h3 className="font-bold text-lg mb-2">Zeitersparnis</h3>
                    <p className="text-neutral-400">Während wir schrauben und aufbauen, können Sie sich um andere wichtige Dinge kümmern.</p>
                  </div>
                  <div>
                    <div className="w-12 h-12 bg-primary flex items-center justify-center mb-4">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"></path></svg>
                    </div>
                    <h3 className="font-bold text-lg mb-2">Profi-Werkzeug</h3>
                    <p className="text-neutral-400">Wir bringen sämtliches benötigtes Spezialwerkzeug und Befestigungsmaterial direkt mit.</p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      }
    />
  );
}
